import { computed, Directive, effect, inject, input } from '@angular/core';
import { MatRipple } from '@angular/material/core';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

export type ButtonColor =
  | 'primary'
  | 'secondary'
  | 'error'
  | 'success'
  | 'white';

const currentIconColorClass = '[&_fc-icon]:!bg-current';
const filledIconColorClass = '[&_fc-icon]:!bg-white';

const buttonVariants = cva(
  'relative flex items-center justify-center font-medium cursor-pointer overflow-hidden outline-none whitespace-nowrap select-none border-none box-border focus-visible:ring-2',
  {
    variants: {
      variant: {
        filled: '',
        tonal: '',
        outline: 'bg-transparent border border-solid border-current',
        text: 'bg-transparent',
        icon: 'bg-transparent',
      },
      color: {
        primary: 'focus-visible:ring-primary-700',
        secondary: 'focus-visible:ring-secondary-700',
        error: 'focus-visible:ring-error-700',
        success: 'focus-visible:ring-success-700',
        white: 'focus-visible:ring-secondary-200 focus-visible:ring-[3px]',
      },
      size: {
        md: '',
        sm: '',
      },
      iconPosition: {
        none: '',
        start: '',
        end: '',
        only: '',
      },
      disabled: {
        true: 'opacity-50 grayscale cursor-not-allowed pointer-events-none shadow-none',
        false: '',
      },
      loading: {
        true: '',
        false: '',
      },
      focused: {
        true: 'ring-2',
        false: '',
      },
    },
    compoundVariants: [
      { focused: true, color: 'primary', class: 'ring-primary-700' },
      { focused: true, color: 'secondary', class: 'ring-secondary-700' },
      { focused: true, color: 'error', class: 'ring-error-700' },
      { focused: true, color: 'success', class: 'ring-success-700' },
      { focused: true, color: 'white', class: 'ring-secondary-200 ring-[3px]' },
      {
        size: 'md',
        class: 'h-40 px-16 text-sm py-10 gap-8 rounded-lg min-w-fit',
      },
      {
        size: 'sm',
        class: 'h-36 px-12 text-sm py-8 gap-4 rounded-lg min-w-fit',
      },
      { size: 'md', iconPosition: 'start', class: 'pl-10' },
      { size: 'md', iconPosition: 'end', class: 'pr-10' },
      { size: 'md', iconPosition: 'only', class: 'px-10' },
      { size: 'sm', iconPosition: 'start', class: 'pl-8' },
      { size: 'sm', iconPosition: 'end', class: 'pr-8' },
      { size: 'sm', iconPosition: 'only', class: 'px-8' },

      // filled + color
      {
        variant: 'filled',
        color: 'primary',
        class: `text-white bg-primary-600 hover:bg-primary-700! active:bg-primary-800 ${filledIconColorClass}`,
      },
      {
        variant: 'filled',
        color: 'secondary',
        class: `text-white bg-secondary-500 hover:bg-secondary-700! active:bg-secondary-700 ${filledIconColorClass}`,
      },
      {
        variant: 'filled',
        color: 'error',
        class: `text-white bg-error-600 hover:bg-error-700! active:bg-error-800 ${filledIconColorClass}`,
      },
      {
        variant: 'filled',
        color: 'success',
        class: `text-white bg-success-600 hover:bg-success-700! active:bg-success-800 ${filledIconColorClass}`,
      },
      {
        variant: 'filled',
        color: 'white',
        class: `text-secondary-600 bg-white hover:bg-secondary-50! active:bg-secondary-50 ${currentIconColorClass}`,
      },

      // tonal + color
      {
        variant: 'tonal',
        color: 'primary',
        class: `bg-primary-100 text-primary-600 hover:bg-primary-200! active:bg-primary-300 ${currentIconColorClass}`,
      },
      {
        variant: 'tonal',
        color: 'secondary',
        class: `bg-secondary-100 text-secondary-500 hover:bg-secondary-200! active:bg-secondary-300 ${currentIconColorClass}`,
      },
      {
        variant: 'tonal',
        color: 'error',
        class: `bg-error-100 text-error-600 hover:bg-error-200! active:bg-error-300 ${currentIconColorClass}`,
      },
      {
        variant: 'tonal',
        color: 'success',
        class: `bg-success-100 text-success-600 hover:bg-success-200! active:bg-success-300 ${currentIconColorClass}`,
      },
      {
        variant: 'tonal',
        color: 'white',
        class: `bg-secondary-900 text-white hover:bg-secondary-800! active:bg-secondary-800 ${currentIconColorClass}`,
      },

      // outline + color
      {
        variant: 'outline',
        color: 'primary',
        class: `text-primary-600 hover:bg-primary-100 active:bg-primary-100 border-primary-300 ${currentIconColorClass}`,
      },
      {
        variant: 'outline',
        color: 'secondary',
        class: `text-secondary-500 hover:bg-secondary-100 active:bg-secondary-100 border-secondary-300 ${currentIconColorClass}`,
      },
      {
        variant: 'outline',
        color: 'error',
        class: `text-error-600 hover:bg-error-100 active:bg-error-100 border-error-300 ${currentIconColorClass}`,
      },
      {
        variant: 'outline',
        color: 'success',
        class: `text-success-600 hover:bg-success-100 active:bg-success-100 border-success-300 ${currentIconColorClass}`,
      },
      {
        variant: 'outline',
        color: 'white',
        class: `text-white hover:bg-secondary-900 active:bg-secondary-900 border-secondary-600 ${currentIconColorClass}`,
      },

      // text + color
      {
        variant: 'text',
        color: 'primary',
        class: `text-primary-600 hover:bg-primary-100 active:bg-primary-100 ${currentIconColorClass}`,
      },
      {
        variant: 'text',
        color: 'secondary',
        class: `text-secondary-500 hover:bg-secondary-100 active:bg-secondary-100 ${currentIconColorClass}`,
      },
      {
        variant: 'text',
        color: 'error',
        class: `text-error-600 hover:bg-error-100 active:bg-error-100 ${currentIconColorClass}`,
      },
      {
        variant: 'text',
        color: 'success',
        class: `text-success-600 hover:bg-success-100 active:bg-success-100 ${currentIconColorClass}`,
      },
      {
        variant: 'text',
        color: 'white',
        class: `text-white hover:bg-secondary-900 active:bg-secondary-900 ${currentIconColorClass}`,
      },

      // icon + color
      {
        variant: 'icon',
        color: 'primary',
        class: `text-primary-600 hover:bg-secondary-500/10 active:bg-primary-100 ${currentIconColorClass}`,
      },
      {
        variant: 'icon',
        color: 'secondary',
        class: `text-secondary-500 hover:bg-secondary-500/10 active:bg-secondary-100 ${currentIconColorClass}`,
      },
      {
        variant: 'icon',
        color: 'error',
        class: `text-error-600 hover:bg-secondary-500/10 active:bg-error-100 ${currentIconColorClass}`,
      },
      {
        variant: 'icon',
        color: 'success',
        class: `text-success-600 hover:bg-secondary-500/10 active:bg-success-100 ${currentIconColorClass}`,
      },
      {
        variant: 'icon',
        color: 'white',
        class: `text-white hover:bg-secondary-900 active:bg-secondary-900 ${currentIconColorClass}`,
      },

      {
        disabled: true,
        class: `bg-secondary-100 text-secondary-200 border-secondary-100 ${currentIconColorClass}`,
      },
      {
        variant: ['text', 'icon'],
        disabled: true,
        class: 'bg-transparent',
      },
      {
        color: 'white',
        disabled: true,
        class: `bg-grey-900 text-grey-600 border-grey-900 opacity-100 grayscale-0 ${currentIconColorClass}`,
      },
      {
        color: 'white',
        loading: true,
        class: `bg-grey-900 text-grey-600 border-grey-900 ${currentIconColorClass}`,
      },
    ],
    defaultVariants: {
      variant: 'filled',
      color: 'primary',
      size: 'md',
      iconPosition: 'none',
      disabled: false,
      loading: false,
    },
  },
);

export type ButtonVariant = 'filled' | 'outline' | 'text' | 'tonal' | 'icon';
export type ButtonSize = 'sm' | 'md';
export type ButtonIconPosition = 'none' | 'start' | 'end' | 'only';

@Directive({
  selector: '[ngButton]',
  exportAs: 'ngButton',
  standalone: true,
  hostDirectives: [MatRipple],
  host: {
    '[class]': 'computedClasses()',
    '[disabled]': 'disabled() || loading() ? true : null',
  },
})
export class NgButtonDirective {
  variant = input<ButtonVariant>('filled');
  size = input<ButtonSize>('md');
  color = input<ButtonColor | undefined>(undefined);
  iconPosition = input<ButtonIconPosition | undefined>(undefined);
  loading = input<boolean>(false);
  disabled = input<boolean>(false);
  focused = input<boolean>(false);

  private _matRipple = inject(MatRipple);

  constructor() {
    effect(() => {
      this._matRipple.color = this.rippleColor();
      this._matRipple.disabled = this.disabled() || this.loading();
    });
  }

  computedClasses = computed(() => {
    return twMerge(
      buttonVariants({
        variant: this.variant(),
        color: this.resolvedColor(),
        size: this.size(),
        iconPosition: this.resolvedIconPosition(),
        disabled: this.disabled(),
        loading: this.loading(),
        focused: this.focused(),
      }),
    );
  });

  rippleColor = computed(() => {
    const variant = this.variant();
    const color = this.resolvedColor();

    let weight = '200';

    if (variant === 'filled') {
      weight = '800';
    } else if (variant === 'tonal') {
      weight = '300';
    }

    if (color === 'white') {
      if (variant === 'filled') {
        return 'color-mix(in srgb, var(--secondary-100) 50%, transparent)';
      }

      if (variant === 'tonal') {
        return 'color-mix(in srgb, var(--secondary-700) 50%, transparent)';
      }

      return 'color-mix(in srgb, var(--secondary-800) 50%, transparent)';
    }

    return `color-mix(in srgb, var(--${color}-${weight}) 50%, transparent)`;
  });

  spinnerColor = computed((): 'primary' | 'secondary' | 'error' | 'white' => {
    if (this.resolvedColor() === 'white') return 'secondary';
    if (this.variant() === 'filled') return 'white';
    if (this.resolvedColor() === 'error') return 'error';
    if (this.resolvedColor() === 'secondary') return 'secondary';
    return 'primary';
  });

  spinnerSize = computed(() => (this.size() === 'sm' ? 14 : 16));

  private resolvedColor = computed((): ButtonColor => {
    return (
      this.color() ??
      (this.resolvedIconPosition() === 'only' ? 'secondary' : 'primary')
    );
  });

  private resolvedIconPosition = computed((): ButtonIconPosition => {
    return this.iconPosition() ?? (this.variant() === 'icon' ? 'only' : 'none');
  });
}
